import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import OwnerNavigation from './OwnerNavigation';
import ManagerDashboard from './ManagerDashboard';
import AnalyticsDashboard from './dashboard/AnalyticsDashboard';
import WaiterManagement from './WaiterManagement';
import ManagerManagement from './ManagerManagement';
import MenuPreview from './MenuPreview';
import ProfileManagement from './ProfileManagement';
import POSApp from './pos/POSApp';
import POSDashboard from './pos/POSDashboard';
import BillingSettings from './pos/BillingSettings';
import BillingPaymentApp from './pos/BillingPaymentApp';
import OrdersApp from './pos/OrdersApp';
import InvoicesApp from './pos/InvoicesApp';
import CRMApp from './pos/CRMApp';
import ReportsApp from './pos/ReportsApp';
import TerminalSetup from './pos/TerminalSetup';
import InventoryApp from './inventory/InventoryApp';

const PATH_TO_TAB = {
  '/dashboard': 'dashboard',
  '/analytics': 'analytics',
  '/preview': 'preview',
  '/waiters': 'waiters',
  '/managers': 'managers',
  '/profile': 'profile',
  '/pos-dashboard': 'pos-dashboard',
  '/pos': 'pos',
  '/orders': 'orders',
  '/invoices': 'invoices',
  '/reports': 'reports',
  '/crm': 'crm',
  '/billing-payment': 'billing-payment',
  '/billing-settings': 'billing-settings',
  '/terminal': 'terminal',
  '/inventory': 'inventory',
};

const OwnerApp = ({ restaurant, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = PATH_TO_TAB[location.pathname] || 'analytics';
  const [restaurantData, setRestaurantData] = useState(restaurant);

  const handleTabChange = (newTab) => navigate(`/${newTab}`);

  useEffect(() => {
    const titles = {
      analytics: 'Analytics', dashboard: 'Menu Management', preview: 'Menu Preview',
      waiters: 'Waiter Management', managers: 'Manager Management', profile: 'Profile',
      'pos-dashboard': 'POS Dashboard', pos: 'POS', orders: 'Orders',
      invoices: 'Invoices', reports: 'Reports', crm: 'Customers',
      'billing-payment': 'Quick Billing', 'billing-settings': 'Billing Settings',
      inventory: 'Inventory',
    };
    document.title = `${titles[activeTab] || 'Menu Management'} - ${restaurantData?.name || 'Menuron'}`;
  }, [activeTab, restaurantData]);

  useEffect(() => {
    if (restaurant) setRestaurantData(restaurant);
  }, [restaurant]);

  useEffect(() => {
    const preventInstall = (e) => { e.preventDefault(); };
    window.addEventListener('beforeinstallprompt', preventInstall);
    return () => window.removeEventListener('beforeinstallprompt', preventInstall);
  }, []);

  const handleUpdateRestaurant = (updatedRestaurant) => setRestaurantData(updatedRestaurant);

  if (restaurantData?.menuron_lite) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '40px 32px', textAlign: 'center', maxWidth: '420px', width: '90%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📱</div>
          <h2 style={{ margin: '0 0 12px', color: '#111', fontSize: '1.4rem', fontWeight: '700' }}>Menuron Lite</h2>
          <p style={{ margin: '0 0 28px', color: '#666', lineHeight: '1.6' }}>
            This restaurant is on the Menuron Lite plan. Please use the Menuron Android app to access your account.
          </p>
          <button
            onClick={onLogout}
            style={{ background: 'linear-gradient(135deg, #FF4444, #FF6B00)', color: 'white', border: 'none', borderRadius: '10px', padding: '12px 28px', fontSize: '1rem', fontWeight: '700', cursor: 'pointer' }}
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <ManagerDashboard restaurant={restaurantData} />;
      case 'analytics':
        return <AnalyticsDashboard restaurant={restaurantData} />;
      case 'preview':
        return <MenuPreview restaurant={restaurantData} />;
      case 'waiters':
        return <WaiterManagement restaurant={restaurantData} />;
      case 'managers':
        return <ManagerManagement restaurant={restaurantData} owner={user} />;
      case 'profile':
        return (
          <ProfileManagement
            restaurant={restaurantData}
            user={user}
            onUpdateRestaurant={handleUpdateRestaurant}
          />
        );
      case 'terminal':
        return <TerminalSetup restaurant={restaurantData} />;
      case 'inventory':
        return <InventoryApp restaurant={restaurantData} />;
      case 'pos-dashboard':
      case 'pos':
      case 'orders':
      case 'invoices':
      case 'reports':
      case 'crm':
      case 'billing-payment':
      case 'billing-settings': {
        if (activeTab === 'pos-dashboard') return <POSDashboard restaurant={restaurantData} />;
        if (activeTab === 'pos') return <POSApp restaurant={restaurantData} />;
        if (activeTab === 'orders') return <OrdersApp restaurant={restaurantData} />;
        if (activeTab === 'invoices') return <InvoicesApp restaurant={restaurantData} />;
        if (activeTab === 'reports') return <ReportsApp restaurant={restaurantData} userType="owner" />;
        if (activeTab === 'crm') return <CRMApp restaurant={restaurantData} canManageDiscounts={true} />;
        if (activeTab === 'billing-payment') return <BillingPaymentApp restaurant={restaurantData} />;
        return <BillingSettings restaurant={restaurantData} />;
      }
      default:
        return <AnalyticsDashboard restaurant={restaurantData} />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <OwnerNavigation
        activeTab={activeTab}
        onTabChange={handleTabChange}
        restaurant={restaurantData}
        onLogout={onLogout}
      />
      {renderContent()}
    </div>
  );
};

export default OwnerApp;
